/**
 * SF-4653: plain-words helpers for a demo build's run (progress and results pages).
 * Wording follows the Eva chat change set card (sf-react-app ChangeSetCard).
 */
import type { DemoBuildRunView, DemoBuildStep, DemoBuildStepStatus } from './api/demoBuildsClient';

export const STEP_STATUS: Record<DemoBuildStepStatus, { label: string; color: string }> = {
  waiting: { label: 'Waiting', color: 'default' },
  running: { label: 'Running', color: 'processing' },
  done: { label: 'Done', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
};

/** Steps in run order, grouped by their group (first appearance keeps the order). */
export function groupSteps(steps: DemoBuildStep[]) {
  const groups = new Map<string, DemoBuildStep[]>();
  steps.forEach((step) => {
    const name = step.group || 'Other changes';
    groups.set(name, [...(groups.get(name) ?? []), step]);
  });
  return [...groups.entries()].map(([group, items]) => ({ group, items }));
}

/** "Making the changes: 3 of 8 done" while building, "Finished" / "Stopped" afterwards. */
export function progressLine(status: string, steps: DemoBuildStep[]) {
  const done = steps.filter((s) => s.status === 'done').length;
  const lead =
    status === 'done' ? 'Finished' : status === 'failed' ? 'Stopped' : 'Making the changes';
  return `${lead}: ${done} of ${steps.length} done`;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "1 company, 2 locations, 3 users, 4 surveys" (only the kinds that were made). */
export function madeSummary(created: DemoBuildRunView['created']): string[] {
  if (!created) return [];
  const out: string[] = [];
  if (created.company_id) out.push('1 company');
  const lists: Array<[string[] | undefined, string, string]> = [
    [created.location_ids, 'location', 'locations'],
    [created.user_ids, 'user', 'users'],
    [created.survey_ids, 'survey', 'surveys'],
  ];
  lists.forEach(([ids, one, many]) => {
    if (ids?.length) out.push(plural(ids.length, one, many));
  });
  return out;
}
